'use client';

import { Check, X, Minus } from 'lucide-react';

type Cell = 'yes' | 'no' | 'partial';

interface Row {
  capability: string;
  detail: string;
  cloudflare: Cell;
  waf: Cell;
  llmFirewall: Cell;
  silker: Cell;
}

const rows: Row[] = [
  {
    capability: 'SQLi / XSS / path traversal',
    detail: 'Classic OWASP attacks on incoming requests',
    cloudflare: 'yes',
    waf: 'yes',
    llmFirewall: 'no',
    silker: 'yes',
  },
  {
    capability: 'Prompt injection & jailbreaks',
    detail: 'Instruction overrides targeting your LLM routes',
    cloudflare: 'no',
    waf: 'no',
    llmFirewall: 'yes',
    silker: 'yes',
  },
  {
    capability: 'Data leak prevention (outbound)',
    detail: 'Secrets, API keys & PII leaving your app in responses',
    cloudflare: 'no',
    waf: 'no',
    llmFirewall: 'partial',
    silker: 'yes',
  },
  {
    capability: 'Supabase / open database detection',
    detail: 'Missing RLS, exposed anon key, unauthenticated routes',
    cloudflare: 'no',
    waf: 'no',
    llmFirewall: 'no',
    silker: 'yes',
  },
  {
    capability: 'App-context threats (IDOR, broken auth)',
    detail: 'Access control flaws specific to your routes',
    cloudflare: 'partial',
    waf: 'partial',
    llmFirewall: 'no',
    silker: 'yes',
  },
  {
    capability: 'Rate limiting & API abuse',
    detail: 'Sliding-window limits, auto IP ban',
    cloudflare: 'yes',
    waf: 'partial',
    llmFirewall: 'no',
    silker: 'yes',
  },
  {
    capability: 'No-code / one-command deploy',
    detail: 'Works without touching app code (Worker or proxy)',
    cloudflare: 'yes',
    waf: 'no',
    llmFirewall: 'no',
    silker: 'yes',
  },
  {
    capability: 'Works without a paid plan',
    detail: 'Full protection on free Vercel / Railway / Render',
    cloudflare: 'no',
    waf: 'no',
    llmFirewall: 'partial',
    silker: 'yes',
  },
  {
    capability: 'Real-time dashboard & AI threat analysis',
    detail: 'Built for developers, not security rule writers',
    cloudflare: 'partial',
    waf: 'partial',
    llmFirewall: 'no',
    silker: 'yes',
  },
  {
    capability: 'Free security scan (no install)',
    detail: 'Paste your URL, get a plain-language report',
    cloudflare: 'no',
    waf: 'no',
    llmFirewall: 'no',
    silker: 'yes',
  },
];

const competitors = [
  {
    key: 'cloudflare' as const,
    label: 'Cloudflare WAF',
    note: 'Great at the network layer, blind to your app logic and AI threats',
  },
  {
    key: 'waf' as const,
    label: 'Standalone WAF',
    note: 'Signature rules only - no understanding of AI or outbound leaks',
  },
  {
    key: 'llmFirewall' as const,
    label: 'LLM Firewall',
    note: 'Guards prompts but ignores your backend, DB and outbound data',
  },
];

function CellIcon({ value }: { value: Cell }) {
  if (value === 'yes') return <Check className="w-4 h-4 text-emerald-400" />;
  if (value === 'no') return <X className="w-4 h-4 text-neutral-600" />;
  return <Minus className="w-4 h-4 text-amber-400" />;
}

export default function WhySilkerSection() {
  return (
    <section id="why" className="sm:py-24 pt-16 pb-16 relative">
      <div className="sm:px-6 lg:px-8 max-w-6xl mx-auto px-4">

        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 text-sm font-medium text-rose-400 px-3 py-1 rounded-none bg-rose-500/10 border border-rose-500/20">
            Why Silker
          </span>
          <h2 className="sm:text-4xl md:text-5xl text-3xl font-semibold tracking-tight text-center mt-4 text-white">
            How Silker compares
          </h2>
          <p className="sm:text-lg text-base text-neutral-400 text-center mt-4 max-w-2xl mx-auto leading-relaxed">
            Every tool below covers part of the problem. Silker is the only one that connects the dots across your whole AI app - runtime, outbound data, AI routes and the database layer.
          </p>
        </div>

        {/* Competitor context pills */}
        <div className="grid sm:grid-cols-3 gap-3 mb-8">
          {competitors.map((c) => (
            <div key={c.key} className="border border-white/5 bg-neutral-900/30 px-4 py-3">
              <div className="text-xs font-semibold text-neutral-300 mb-1">{c.label}</div>
              <div className="text-xs text-neutral-500 leading-relaxed">{c.note}</div>
            </div>
          ))}
        </div>

        {/* Comparison table */}
        <div className="rounded-none border border-white/10 bg-neutral-900/30 overflow-hidden">

          {/* Header */}
          <div className="hidden sm:grid grid-cols-12 px-5 sm:px-6 py-3 border-b border-white/10 bg-black/20 text-xs font-semibold uppercase tracking-wider text-neutral-500">
            <div className="col-span-4">Capability</div>
            <div className="col-span-2 text-center">Cloudflare WAF</div>
            <div className="col-span-2 text-center">Standalone WAF</div>
            <div className="col-span-2 text-center">LLM Firewall</div>
            <div className="col-span-2 text-center text-rose-400">Silker</div>
          </div>

          {rows.map((row, index) => (
            <div
              key={index}
              className="grid grid-cols-12 items-center px-5 sm:px-6 py-4 border-b border-white/5 last:border-b-0 hover:bg-white/[0.02] transition-colors"
            >
              <div className="col-span-12 sm:col-span-4 mb-2 sm:mb-0 pr-3">
                <div className="text-sm font-medium text-white">{row.capability}</div>
                <div className="text-xs text-neutral-500 mt-0.5 leading-relaxed">{row.detail}</div>
              </div>

              {/* Mobile: inline labels */}
              <div className="col-span-12 sm:hidden grid grid-cols-4 mt-1 mb-1 gap-1 text-[10px] text-neutral-500 text-center">
                <div>Cloudflare</div>
                <div>WAF</div>
                <div>LLM FW</div>
                <div className="text-rose-400 font-semibold">Silker</div>
              </div>

              <div className="col-span-3 sm:col-span-2 flex justify-center">
                <CellIcon value={row.cloudflare} />
              </div>
              <div className="col-span-3 sm:col-span-2 flex justify-center">
                <CellIcon value={row.waf} />
              </div>
              <div className="col-span-3 sm:col-span-2 flex justify-center">
                <CellIcon value={row.llmFirewall} />
              </div>
              <div className="col-span-3 sm:col-span-2 flex justify-center">
                <div className="p-1.5 rounded-none bg-rose-500/5 border border-rose-500/20">
                  <CellIcon value={row.silker} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-6 mt-5 text-xs text-neutral-500">
          <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-400" /> Yes</span>
          <span className="flex items-center gap-1.5"><Minus className="w-3.5 h-3.5 text-amber-400" /> Partial</span>
          <span className="flex items-center gap-1.5"><X className="w-3.5 h-3.5 text-neutral-600" /> No</span>
        </div>

        <p className="text-center text-xs text-neutral-500 mt-4">
          Already on Cloudflare? Keep it - Silker adds the AI-native layer on top as a Worker or one line of code.
        </p>
      </div>
    </section>
  );
}
